/**
 * cover.ts — #gn-app-cover overlay helpers
 *
 * The overlay element lives in public/index.html as a static div (display:none).
 * CSS classes drive visibility and transitions (see public.css):
 *   gn-cover-visible  → opacity 1, pointer-events auto  (instant show)
 *   gn-cover-out      → opacity 0, pointer-events none  (0.35s fade)
 *
 * A `_hideTimer` property is stored on the element so that showCover() can
 * cancel a pending hide scheduled by SuspenseCover before AuthorizedComponent
 * has had a chance to keep the cover up while CDN stylesheets finish loading.
 */

const COVER_ID     = 'gn-app-cover';
const DOT_ID       = 'gn-app-cover-dot';
const FADE_DURATION = 400; // must match transition duration in public.css

type CoverElement = HTMLElement & { _hideTimer?: ReturnType<typeof setTimeout> | null };

/** Returns the cover element, creating it if it doesn't exist yet. */
export function getCover(): CoverElement {
  let cover = document.getElementById(COVER_ID) as CoverElement | null;
  if (!cover) {
    cover = document.createElement('div') as CoverElement;
    cover.id = COVER_ID;
    const dot = document.createElement('div');
    dot.id = DOT_ID;
    cover.appendChild(dot);
    document.body.appendChild(cover);
  }
  return cover;
}

/**
 * showCover()
 * Instantly makes the overlay visible.
 * Cancels any pending hideCover() timer so the cover stays up
 * while e.g. CDN stylesheets are still loading.
 */
export function showCover(): void {
  const cover = getCover();
  if (cover._hideTimer) {
    clearTimeout(cover._hideTimer);
    cover._hideTimer = null;
  }
  cover.classList.remove('gn-cover-out');
  cover.style.display = 'flex';
  cover.classList.add('gn-cover-visible');
}

/**
 * hideCover()
 * Fades the overlay out over FADE_DURATION ms, then hides it.
 * Stores the timer on the element so showCover() can cancel it.
 */
export function hideCover(): void {
  const cover = document.getElementById(COVER_ID) as CoverElement | null;
  if (!cover) return;
  cover.classList.remove('gn-cover-visible');
  cover.classList.add('gn-cover-out');
  cover._hideTimer = setTimeout(() => {
    cover.classList.remove('gn-cover-out');
    cover.style.display = 'none';
    cover._hideTimer = null;
  }, FADE_DURATION);
}
